/**
 * ReleasePathSelector.js
 * Choix du chemin de remise du code de transmission — PF-03 CP3.1 (DEC-037).
 * Cartes radio (role radiogroup / radio, même patron que PrioritySelector),
 * une par chemin non masqué de RELEASE_PATHS ; un chemin coming_soon est
 * affiché désactivé.
 */

import React from 'react';
import { RELEASE_PATHS } from '../config/releasePathConfig';

const S = '#00F5A0';
const G = '#8892A4';

const IconPrinter = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

const IconLandmark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="22" x2="21" y2="22" />
    <line x1="6" y1="18" x2="6" y2="11" />
    <line x1="10" y1="18" x2="10" y2="11" />
    <line x1="14" y1="18" x2="14" y2="11" />
    <line x1="18" y1="18" x2="18" y2="11" />
    <polygon points="12 2 20 7 4 7" />
  </svg>
);

const IconKey = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6" />
    <path d="M15.5 7.5l3 3L22 7l-3-3" />
  </svg>
);

const PATH_ICONS = { self_custody: IconPrinter, notary: IconLandmark };

const ReleasePathSelector = ({ value, onChange, disabled = false }) => (
  <div
    role="radiogroup"
    aria-label="Chemin de remise du code de transmission"
    style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
  >
    {RELEASE_PATHS.filter((p) => p.availability !== 'hidden').map((p) => {
      const comingSoon = p.availability === 'coming_soon';
      const inactive = disabled || comingSoon;
      const active = value === p.value;
      const Icon = PATH_ICONS[p.value] || IconKey;
      return (
        <button
          key={p.value}
          type="button"
          role="radio"
          aria-checked={active}
          disabled={inactive}
          onClick={() => onChange(p.value)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            width: '100%',
            textAlign: 'left',
            padding: '14px 16px',
            borderRadius: 12,
            cursor: inactive ? 'not-allowed' : 'pointer',
            border: active ? `1px solid ${S}` : '1px solid rgba(255,255,255,0.08)',
            background: active ? 'rgba(0,245,160,0.08)' : 'rgba(255,255,255,0.04)',
            opacity: comingSoon ? 0.55 : 1,
            color: '#fff',
            transition: 'all 0.15s ease',
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: active ? S : 'rgba(255,255,255,0.06)',
              color: active ? '#0A1628' : G,
            }}
          >
            <Icon />
          </span>
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: active ? S : '#fff' }}>
              {p.label}
            </span>
            <span style={{ display: 'block', fontSize: 12, color: G, marginTop: 3, lineHeight: 1.5 }}>
              {p.description}
            </span>
            {comingSoon && (
              <span
                style={{
                  display: 'inline-block',
                  marginTop: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: G,
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 6,
                  padding: '2px 8px',
                }}
              >
                Bientôt disponible
              </span>
            )}
          </span>
        </button>
      );
    })}
  </div>
);

export default ReleasePathSelector;
